import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TabsModule } from 'primeng/tabs';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { map, switchMap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { RilevamentoScambiDoppiService } from '@/core/service/rilevamento-doppi.service';
import { RilevamentoScambiIntersezioneDoppiService } from '@/core/service/rilevamento-intersezione.service';
import { L94Rfil94Service } from '@/core/service/l94_rfil_94.service';
import { RilevamentoScambiSempliciService } from '@/core/service/rilevamento-semplici.service';
import { DeviatoioService } from '@/core/service/deviatoio.service';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ChartModule, UIChart } from 'primeng/chart';
import { Chart } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';

// Registra il plugin di zoom/pan sul registry condiviso di Chart.js (usato da p-chart).
Chart.register(zoomPlugin);

@Component({
    selector: 'app-storico',
    standalone: true,
    imports: [CommonModule, FormsModule, TabsModule, CardModule, TableModule, TagModule, SelectModule, ButtonModule, CheckboxModule, ChartModule],
    templateUrl: './storico.component.html',
    styles: [
        `
            :host ::ng-deep th {
                text-align: center !important;
            }
            /* Cella valore: griglia 1fr | tag | 1fr così il tag resta centrato e
               allineato alle colonne, e la freccia sta a destra senza sovrapporsi. */
            :host ::ng-deep td > .flex {
                display: grid;
                grid-template-columns: 1fr auto 1fr;
                align-items: center;
                width: 100%;
                gap: 0;
            }
            :host ::ng-deep td > .flex > p-tag {
                grid-column: 2;
            }
            :host ::ng-deep td > .flex > i {
                grid-column: 3;
                justify-self: start;
                margin-left: 0.25rem;
            }

            /* ---- Sezione "Andamento nel tempo" (grafico) — niente box/card ---- */
            .chart-section {
                margin-top: 2rem;
                padding-top: 1.5rem;
                border-top: 1px solid var(--surface-border);
            }
            .chart-toggle {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 0.9rem 1.1rem;
                background: color-mix(in srgb, var(--primary-color) 6%, var(--surface-card));
                border: 1px solid var(--surface-border);
                border-radius: 0.7rem;
                cursor: pointer;
                font-size: 1.05rem;
                color: var(--text-color);
                transition: background 0.12s ease, border-color 0.12s ease;
            }
            .chart-toggle:hover {
                background: color-mix(in srgb, var(--primary-color) 12%, var(--surface-card));
                border-color: color-mix(in srgb, var(--primary-color) 35%, var(--surface-border));
            }
            .chart-toggle i.pi-chart-line,
            .chart-toggle i.pi-history {
                color: var(--primary-color);
            }
            .storico-toggle {
                width: 100%;
            }
            .chart-body {
                margin-top: 1.25rem;
            }
            .chart-controls {
                display: flex;
                flex-wrap: wrap;
                align-items: flex-end;
                gap: 1rem 1.5rem;
                margin-bottom: 1rem;
            }
            .chart-ctrl {
                display: flex;
                flex-direction: column;
                gap: 0.35rem;
                min-width: 12rem;
            }
            .chart-ctrl label {
                font-size: 0.8rem;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.04em;
                color: var(--text-color-secondary);
            }
            .chart-ctrl-inline {
                flex-direction: row;
                align-items: center;
                gap: 0.5rem;
                min-width: 0;
            }
            .chart-ctrl-spacer {
                margin-left: auto;
            }
            /* Contenitore ad altezza fissa: il canvas Chart.js (maintainAspectRatio:false)
               riempie il genitore senza sbordare. p-chart in absolute inset:0. */
            .chart-wrap {
                position: relative;
                width: 100%;
                height: 440px;
                overflow: hidden;
            }
            :host ::ng-deep .chart-wrap p-chart {
                position: absolute;
                inset: 0;
                display: block;
            }
            :host ::ng-deep .chart-wrap p-chart > div {
                position: relative;
                width: 100%;
                height: 100%;
            }
            .chart-legend-hint {
                display: flex;
                flex-wrap: wrap;
                gap: 1rem;
                margin-top: 0.75rem;
                font-size: 0.8rem;
                color: var(--text-color-secondary);
            }
            .chart-legend-hint .dot {
                display: inline-block;
                width: 0.7rem;
                height: 0.7rem;
                border-radius: 50%;
                margin-right: 0.35rem;
                vertical-align: middle;
            }
        `
    ]
})
export class StoricoComponent implements OnInit, OnChanges {
    @Input() deviatoioId!: number;
    @Input() normativaId!: number;
    @Input() tipoDeviatoio!: number;

    rilevamenti: any[] = [];
    normativa: any = null;
    normativeTabs: { [key: string]: any } = {};

    // Colonne per ciascun tipo
    columnsSemplice: string[] = [];
    columnsRamo1: string[] = [];
    columnsRamo2: string[] = [];
    columnsCuore: string[] = [];
    columnsTelaio: Record<string, string[]> = {};

    // Tabs attive
    activeTab = 'ramo1';
    activeTabTelaio = 'telaio1';

    telai: { label: string; value: string }[] = [];
    deviatoiOptions: { label: string; value: number }[] = [];

    // Tabella storico collassabile (aperta di default)
    showStorico = true;

    // ---- Grafico "Andamento nel tempo" (collassato di default = non invasivo) ----
    @ViewChild(UIChart) chartRef?: UIChart;
    showChart = false;
    fitTolerance = false;
    chartGroups: { label: string; prefix: string; tabKey: string }[] = [];
    selectedGroupPrefix: string | null = null;
    selectedColumn: string | null = null;
    chartData: any = null;
    chartOptions: any = null;

    constructor(
        private scambiDoppiService: RilevamentoScambiDoppiService,
        private scambiIntersezioneService: RilevamentoScambiIntersezioneDoppiService,
        private scambiSempliciService: RilevamentoScambiSempliciService,
        private l94Service: L94Rfil94Service,
        private deviatoiRiepilogoGeneraleService: DeviatoioService,
        private route: ActivatedRoute,
        private router: Router
    ) {}

    // 🔹 Caricamento iniziale (solo se arrivano query params)
    ngOnInit(): void {
        const qDeviatoio = +(this.route.snapshot.queryParamMap.get('deviatoio') ?? 0);
        const qNormativa = +(this.route.snapshot.queryParamMap.get('normativa') ?? 0);
        const qTipo = +(this.route.snapshot.queryParamMap.get('tipoDeviatoio') ?? 0);

        // se arrivano da URL → priorità alta
        if (qDeviatoio && qNormativa && qTipo) {
            this.deviatoioId = qDeviatoio;
            this.normativaId = qNormativa;
            this.tipoDeviatoio = qTipo;
        }

        // se tutti i dati sono già pronti (caso pagina diretta)
        if (this.deviatoioId && this.normativaId && this.tipoDeviatoio) {
            this.loadNormativaAndRilevamenti();
        }
    }

    // 🔹 Reagisci quando gli Input cambiano (caso dialog)
    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['deviatoioId'] || changes['normativaId'] || changes['tipoDeviaoio']) && this.deviatoioId && this.normativaId && this.tipoDeviatoio) {
            this.loadNormativaAndRilevamenti();
        }
    }

    // 🔹 Carica la lista dei deviatoi (solo una volta)
    loadDeviatoi(): void {
        this.deviatoiRiepilogoGeneraleService
            .list()
            .pipe(map((res) => res.body ?? []))
            .subscribe({
                next: (list) => {
                    this.deviatoiOptions = list.map((d: any) => ({
                        label: `${d.identificativo} - ${d.deviatoioTipologia?.labelFe ?? ''}`,
                        value: d.id
                    }));
                },
                error: (err) => console.error('Errore caricamento deviatoi:', err)
            });
    }

    // Quando l’utente cambia deviatoio dal menu
    onDeviatoioChange(event: any): void {
        const newId = event.value;
        if (!newId) return;

        this.deviatoioId = newId;

        this.deviatoiRiepilogoGeneraleService.get(newId).subscribe({
            next: (dev) => {
                this.tipoDeviatoio = dev?.deviatoioTipologia?.id ?? 0;

                // se la normativa resta la stessa, posso richiamare direttamente il caricamento
                if (this.normativaId && this.tipoDeviatoio) {
                    this.loadNormativaAndRilevamenti();
                }
                console.log('dev ', dev);
                // se la pagina è navigabile, aggiorno anche i parametri URL
                if (this.router && this.route.snapshot.routeConfig?.path?.includes('storico')) {
                    console.log('navigating ');
                    this.router.navigate([], {
                        queryParams: {
                            deviatoio: newId,
                            normativa: this.normativaId,
                            tipoDeviatoio: this.tipoDeviatoio
                        },
                        queryParamsHandling: 'merge'
                    });
                }
            },
            error: () => {}
        });
    }

    // 🔹 Carica normativa + storico
    loadNormativaAndRilevamenti(): void {
        if (!this.deviatoioId || !this.normativaId || !this.tipoDeviatoio) return;

        this.l94Service
            .getL94Rfil94Byid(this.normativaId)
            .pipe(
                switchMap((norme) => {
                    // Caso: la normativa ha più tab
                    console.log('norme ', norme);
                    if (Array.isArray(norme)) {
                        this.normativeTabs = norme.reduce((acc: any, curr: any) => {
                            const tabKey = curr.tab ? `tab${curr.tab}` : 'tab1';
                            acc[tabKey] = curr;
                            return acc;
                        }, {});
                        this.normativa = this.normativeTabs['tab1']; // default tab1
                        console.log('this.normativa ', this.normativa);
                    } else {
                        this.normativa = norme;
                        this.normativeTabs = { tab1: norme };
                    }


                    if (this.tipoDeviatoio === 1) return this.scambiSempliciService.list();
                    if (this.tipoDeviatoio === 2) return this.scambiDoppiService.list();
                    return this.scambiIntersezioneService.list();
                })
            )
            .subscribe({
                next: (data) => {
                    this.rilevamenti = data.filter((d) => d.deviatoiRiepilogoGenerale?.id === this.deviatoioId);
                    if (!this.rilevamenti.length) return;

                    const example = this.rilevamenti[0];
                    console.log('example ', example , ' tipoDeviatoio ', this.tipoDeviatoio);
                    if (this.tipoDeviatoio === 1) {
                        this.columnsSemplice = this.extractSimpleColumns(example);
                        this.activeTab = 'tab1';
                    } else if (this.tipoDeviatoio === 2) {
                        this.columnsRamo1 = this.extractColumns(example, 'ramo1');
                        this.columnsRamo2 = this.extractColumns(example, 'ramo2');
                        this.columnsCuore = this.extractColumns(example, 'cuoreInterno');
                        this.activeTab = 'tab1';
                    } else if (this.tipoDeviatoio === 3) {
                        this.prepareTelai(example);
                        this.activeTabTelaio = 'tab1';
                    }

                    this.setupChart();
                },
                error: (err) => console.error('Errore caricamento storico:', err)
            });
    }

    // =============================== FUNZIONI UTILI ===============================

    extractColumns(r: any, prefix: string): string[] {
        return Object.keys(r)
            .filter((k) => k.startsWith(prefix))
            .map((k) => k.replace(prefix, ''))
            .map((k) => k.charAt(0).toUpperCase() + k.slice(1));
    }

    extractSimpleColumns(r: any): string[] {
        const keys = Object.keys(r);
        const sCols = keys.filter((k) => /^s\d+$/i.test(k)).map((k) => k.toUpperCase());
        const fixedCols = ['A', 'B', 'C', 'D', 'P', 'Q'];
        return [...sCols, ...fixedCols];
    }

    prepareTelai(r: any): void {
        const allKeys = Object.keys(r);
        const prefixes = Array.from(new Set(allKeys.map((k) => k.match(/^(telaio\d+)/i)?.[1]).filter(Boolean))) as string[];

        this.telai = prefixes.map((p) => ({
            label: p.replace('telaio', 'Telaio '),
            value: p
        }));

        prefixes.forEach((p) => {
            const cols = allKeys.filter((k) => k.startsWith(p)).map((k) => k.replace(`${p}`, '').toUpperCase());
            this.columnsTelaio[p] = cols;
        });
    }

    getRows(prefix: string): any[] {
        if (!this.rilevamenti.length) return [];

        if (prefix === 'semplice') {
            return this.rilevamenti
                .map((r) => {
                    const row: any = { data: r.dataRilevamento };
                    this.columnsSemplice.forEach((col) => {
                        const key = col.toLowerCase();
                        row[col] = r[key] ?? '-';
                    });
                    return row;
                })
                .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());
        }

        if (['ramo1', 'ramo2', 'cuoreInterno'].includes(prefix)) {
            const cols = prefix === 'ramo1' ? this.columnsRamo1 : prefix === 'ramo2' ? this.columnsRamo2 : this.columnsCuore;
            return this.rilevamenti
                .map((r) => {
                    const row: any = { data: r.dataRilevamento };
                    cols.forEach((col) => {
                        const key = `${prefix}${col}`;
                        row[col] = r[key] ?? '-';
                    });
                    return row;
                })
                .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());
        }

        if (prefix.startsWith('telaio')) {
            const cols = this.columnsTelaio[prefix] ?? [];
            return this.rilevamenti
                .map((r) => {
                    const row: any = { data: r.dataRilevamento };
                    cols.forEach((col) => {
                        const key = `${prefix}${col}`;
                        row[col] = r[key] ?? '-';
                    });
                    return row;
                })
                .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());
        }

        return [];
    }

    getSeverity(col: string, value: any, tabKey?: string): string {
        // Recupera la normativa corretta
        const normativaCorrente = tabKey ? this.getNormativaForTab(tabKey) : this.normativa;
        if (!normativaCorrente || value === '-' || value === null) return 'secondary';

        const valNum = Number(value);
        if (isNaN(valNum)) return 'secondary';

        // Normalizza la chiave (rimuove prefissi tipo ramo1, telaio3, cuoreInterno)
        const cleanCol = col.replace(/^(ramo\d+|telaio\d+|cuoreInterno)/i, '');
        const keyBase = cleanCol.toUpperCase();

        // Ricava i valori dalla normativa corrente
        const normVal = normativaCorrente[keyBase.toLowerCase()];
        const minVal = normativaCorrente[`min${keyBase}`];
        const plusVal = normativaCorrente[`plus${keyBase}`];

        // Caso con range (min / plus)
        if (normVal !== undefined && minVal !== undefined && plusVal !== undefined) {
            const minRange = normVal - minVal;
            const maxRange = normVal + plusVal;
            return valNum >= minRange && valNum <= maxRange ? 'success' : 'danger';
        }

        // Caso con operatore (opX)
        const lowerKey = keyBase.toLowerCase();
        const opKey = `op${keyBase}`;
        const refVal = Number(normativaCorrente[lowerKey]);
        const op = normativaCorrente[opKey];

        if (refVal !== undefined && op) {
            switch (op.trim()) {
                case '>':
                    return valNum > refVal ? 'success' : 'danger';
                case '>=':
                    return valNum >= refVal ? 'success' : 'danger';
                case '<':
                    return valNum < refVal ? 'success' : 'danger';
                case '<=':
                    return valNum <= refVal ? 'success' : 'danger';
                case '=':
                case '==':
                    return valNum === refVal ? 'success' : 'danger';
            }
        }

        return 'secondary';
    }

    getTrendIcon(col: string, value: any, tabKey?: string): string {
        // Recupera la normativa corretta
        const normativaCorrente = tabKey ? this.getNormativaForTab(tabKey) : this.normativa;
        if (!normativaCorrente || value === '-' || value === null) return '–';

        const valNum = Number(value);
        if (isNaN(valNum)) return '–';

        // Normalizza la chiave: rimuovi eventuali prefissi (ramo1, telaio2, ecc.)
        const normKey = col.replace(/^(ramo\d+|cuoreInterno|telaio\d+)/i, '').toLowerCase();
        const normVal = Number(normativaCorrente[normKey]);
        if (isNaN(normVal)) return '–';

        if (valNum > normVal) return '↑';
        if (valNum < normVal) return '↓';
        return '=';
    }

    getNormativaForTab(tabKey: string): any {
        return this.normativeTabs?.[tabKey] ?? this.normativa;
    }

    // =============================== GRAFICO ANDAMENTO ===============================

    /** Colonne (misure) disponibili per un certo gruppo/prefix. */
    columnsForPrefix(prefix: string | null): string[] {
        if (!prefix) return [];
        if (prefix === 'semplice') return this.columnsSemplice;
        if (prefix === 'ramo1') return this.columnsRamo1;
        if (prefix === 'ramo2') return this.columnsRamo2;
        if (prefix === 'cuoreInterno') return this.columnsCuore;
        return this.columnsTelaio[prefix] ?? [];
    }

    get chartGroupOptions(): { label: string; value: string }[] {
        return this.chartGroups.map((g) => ({ label: g.label, value: g.prefix }));
    }

    get chartColumnOptions(): { label: string; value: string }[] {
        return this.columnsForPrefix(this.selectedGroupPrefix).map((c) => ({ label: c, value: c }));
    }

    /** Costruisce i gruppi selezionabili in base al tipo e imposta i default. */
    setupChart(): void {
        if (this.tipoDeviatoio === 1) {
            this.chartGroups = [{ label: 'Misure', prefix: 'semplice', tabKey: 'tab1' }];
        } else if (this.tipoDeviatoio === 2) {
            this.chartGroups = [
                { label: 'Ramo 1', prefix: 'ramo1', tabKey: 'tab1' },
                { label: 'Ramo 2', prefix: 'ramo2', tabKey: 'tab2' },
                { label: 'Cuore', prefix: 'cuoreInterno', tabKey: 'tab3' }
            ];
        } else {
            this.chartGroups = this.telai.map((t, i) => ({ label: t.label, prefix: t.value, tabKey: `tab${i + 1}` }));
        }

        // Mantieni la selezione precedente se ancora valida, altrimenti default al primo
        if (!this.chartGroups.some((g) => g.prefix === this.selectedGroupPrefix)) {
            this.selectedGroupPrefix = this.chartGroups[0]?.prefix ?? null;
        }
        const cols = this.columnsForPrefix(this.selectedGroupPrefix);
        if (!this.selectedColumn || !cols.includes(this.selectedColumn)) {
            this.selectedColumn = cols[0] ?? null;
        }

        this.buildChart();
    }

    onChartGroupChange(): void {
        const cols = this.columnsForPrefix(this.selectedGroupPrefix);
        this.selectedColumn = cols[0] ?? null;
        this.buildChart();
    }

    /** Limiti di tolleranza per una misura: banda (min/plus) o soglia (operatore). */
    private getBounds(col: string, tabKey: string): { mode: 'range' | 'threshold' | 'none'; lower?: number; upper?: number; ref?: number; opLabel?: string } {
        const norm = this.getNormativaForTab(tabKey);
        if (!norm) return { mode: 'none' };

        const keyBase = col.toUpperCase();
        const normVal = norm[keyBase.toLowerCase()];
        const minVal = norm[`min${keyBase}`];
        const plusVal = norm[`plus${keyBase}`];

        if (normVal !== undefined && normVal !== null && minVal !== undefined && minVal !== null && plusVal !== undefined && plusVal !== null) {
            return { mode: 'range', lower: Number(normVal) - Number(minVal), upper: Number(normVal) + Number(plusVal) };
        }

        const ref = Number(norm[keyBase.toLowerCase()]);
        const op = norm[`op${keyBase}`];
        if (!isNaN(ref) && op) {
            return { mode: 'threshold', ref, opLabel: String(op).trim() };
        }
        return { mode: 'none' };
    }

    /** Costruisce data/options Chart.js per la misura selezionata. */
    buildChart(): void {
        const prefix = this.selectedGroupPrefix;
        const col = this.selectedColumn;
        if (!prefix || !col) {
            this.chartData = null;
            return;
        }

        const group = this.chartGroups.find((g) => g.prefix === prefix);
        const tabKey = group?.tabKey ?? 'tab1';

        // righe in ordine cronologico crescente (getRows ordina decrescente)
        const rows = [...this.getRows(prefix)].sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime());
        if (!rows.length) {
            this.chartData = null;
            return;
        }

        const labels = rows.map((r) => new Date(r.data).toLocaleDateString('it-IT'));
        const values = rows.map((r) => {
            const n = Number(r[col]);
            return isNaN(n) ? null : n;
        });
        const pointColors = rows.map((r) => (this.getSeverity(col, r[col], tabKey) === 'danger' ? '#ef4444' : '#22c55e'));

        const bounds = this.getBounds(col, tabKey);
        const datasets: any[] = [];

        if (bounds.mode === 'range') {
            datasets.push({
                label: 'Limite max',
                data: labels.map(() => bounds.upper),
                borderColor: 'rgba(34,197,94,0.55)',
                borderDash: [5, 5],
                borderWidth: 1,
                pointRadius: 0,
                fill: false,
                tension: 0
            });
            datasets.push({
                label: 'Limite min',
                data: labels.map(() => bounds.lower),
                borderColor: 'rgba(34,197,94,0.55)',
                borderDash: [5, 5],
                borderWidth: 1,
                pointRadius: 0,
                backgroundColor: 'rgba(34,197,94,0.12)',
                fill: '-1',
                tension: 0
            });
        } else if (bounds.mode === 'threshold') {
            datasets.push({
                label: `Soglia (${bounds.opLabel} ${bounds.ref})`,
                data: labels.map(() => bounds.ref),
                borderColor: '#f59e0b',
                borderDash: [5, 5],
                borderWidth: 1,
                pointRadius: 0,
                fill: false,
                tension: 0
            });
        }

        datasets.push({
            label: `Misura ${col}`,
            data: values,
            borderColor: '#3b82f6',
            backgroundColor: '#3b82f6',
            pointBackgroundColor: pointColors,
            pointBorderColor: pointColors,
            pointRadius: 5,
            pointHoverRadius: 7,
            borderWidth: 2,
            tension: 0.25,
            fill: false,
            spanGaps: true
        });

        // Scala Y: di default automatica; con "adatta alla tolleranza" si stringe
        // attorno alla banda/soglia per rendere leggibili tolleranze molto piccole.
        const yScale: any = { beginAtZero: false, grid: { color: 'rgba(0,0,0,0.06)' } };
        if (this.fitTolerance && bounds.mode === 'range' && bounds.lower != null && bounds.upper != null) {
            const pad = Math.max((bounds.upper - bounds.lower) * 1.5, 1);
            yScale.min = bounds.lower - pad;
            yScale.max = bounds.upper + pad;
        } else if (this.fitTolerance && bounds.mode === 'threshold' && bounds.ref != null) {
            const pad = Math.max(Math.abs(bounds.ref) * 0.15, 5);
            yScale.min = bounds.ref - pad;
            yScale.max = bounds.ref + pad;
        }

        this.chartData = { labels, datasets };
        this.chartOptions = {
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: { position: 'top', labels: { usePointStyle: true } },
                tooltip: { mode: 'index', intersect: false },
                zoom: {
                    zoom: {
                        wheel: { enabled: true },
                        pinch: { enabled: true },
                        drag: { enabled: true, backgroundColor: 'rgba(59,130,246,0.15)', borderColor: 'rgba(59,130,246,0.6)', borderWidth: 1 },
                        mode: 'xy'
                    },
                    pan: { enabled: true, mode: 'xy', modifierKey: 'ctrl' }
                }
            },
            scales: {
                x: { grid: { display: false } },
                y: yScale
            }
        };
    }

    onFitToleranceChange(): void {
        this.resetZoom();
        this.buildChart();
    }

    resetZoom(): void {
        const chart: any = this.chartRef?.chart;
        if (chart?.resetZoom) chart.resetZoom();
    }
}
